
import { useState } from 'react';
import { toast } from 'sonner';

interface ImageHandlerOptions {
  maxSize?: number;
  thumbnailWidth?: number;
  acceptedTypes?: string[];
}

export function useImageHandler(options: ImageHandlerOptions = {}) {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB par défaut
    thumbnailWidth = 800,
    acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  } = options;

  const [image, setImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Valider le fichier
  const validateFile = (file: File): boolean => {
    if (!acceptedTypes.includes(file.type)) {
      setError('Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      toast.error('Type de fichier non supporté');
      return false;
    }

    if (file.size > maxSize) {
      setError(`Fichier trop volumineux. Maximum ${Math.round(maxSize / (1024 * 1024))}MB.`);
      toast.error('Fichier trop volumineux');
      return false;
    }

    return true;
  };

  // Redimensionner une image
  const resizeImage = (file: File, maxWidth: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculer les nouvelles dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;

        // Configurer le canvas
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Dessiner l'image redimensionnée
        ctx?.drawImage(img, 0, 0, newWidth, newHeight);

        // Convertir en data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        resolve(dataUrl);
      };

      img.onerror = () => reject(new Error('Erreur lors du chargement de l\'image'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Gérer le changement de fichier
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    try {
      if (!validateFile(file)) {
        return;
      }

      // Redimensionner l'image
      const resizedImage = await resizeImage(file, thumbnailWidth);
      setImage(resizedImage);
      toast.success('Image chargée avec succès');

    } catch (err) {
      console.error('Erreur lors du traitement de l\'image:', err);
      setError('Erreur lors du traitement de l\'image');
      toast.error('Erreur lors du traitement de l\'image');
    } finally {
      setIsProcessing(false);
    }
  };

  // Capturer depuis vidéo
  const captureFromVideo = async (videoElement: HTMLVideoElement): Promise<boolean> => {
    setIsProcessing(true);
    setError(null);

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Impossible de créer le contexte canvas');
      }

      // Configurer le canvas avec les dimensions de la vidéo
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      // Dessiner la frame actuelle de la vidéo
      ctx.drawImage(videoElement, 0, 0);

      // Convertir en data URL
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setImage(dataUrl);
      
      return true;

    } catch (err) {
      console.error('Erreur lors de la capture:', err);
      setError('Erreur lors de la capture de l\'image');
      toast.error('Erreur lors de la capture');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // Réinitialiser
  const resetImage = () => {
    setImage(null);
    setError(null);
  };

  return {
    image,
    isProcessing,
    error,
    handleFileChange,
    captureFromVideo,
    resetImage
  };
}
